import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  subscribeToAllReviews,
  subscribeToReviewContacts,
  updateReviewStatus,
} from "../services/reviewService";
import { logout } from "../services/authService";
import { useAuth } from "../context/AuthContext";
import AdminNav from "../components/AdminNav";
import StarRating from "../components/StarRating";
import { LockIcon } from "../components/icons";
import styles from "./AdminDashboard.module.css";
import reviewStyles from "./AdminReviews.module.css";

const TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "published", label: "Published" },
  { key: "hidden", label: "Hidden" },
];

const BADGE_CLASS = {
  pending: "badgePending",
  published: "badgeConfirmed",
  hidden: "badgeCancelled",
};

function formatCreatedAt(ts) {
  if (!ts?.toDate) return "";
  return ts.toDate().toLocaleString("en-NZ", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AdminReviews() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [contacts, setContacts] = useState({});
  const [loading, setLoading] = useState(true);
  const [permissionError, setPermissionError] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [pendingActionId, setPendingActionId] = useState(null);

  useEffect(() => {
    const unsubReviews = subscribeToAllReviews(
      (data) => {
        setReviews(data);
        setLoading(false);
        setPermissionError(false);
      },
      () => {
        setLoading(false);
        setPermissionError(true);
      }
    );
    const unsubContacts = subscribeToReviewContacts(
      (map) => setContacts(map),
      () => {} // contacts failing is covered by the same permission banner
    );
    return () => {
      unsubReviews();
      unsubContacts();
    };
  }, []);

  const filtered = useMemo(() => {
    if (activeTab === "all") return reviews;
    return reviews.filter((r) => r.status === activeTab);
  }, [reviews, activeTab]);

  const counts = useMemo(() => {
    const c = { all: reviews.length };
    for (const tab of TABS) {
      if (tab.key === "all") continue;
      c[tab.key] = reviews.filter((r) => r.status === tab.key).length;
    }
    return c;
  }, [reviews]);

  const handleStatusChange = async (id, status) => {
    setPendingActionId(id);
    try {
      await updateReviewStatus(id, status);
    } catch (err) {
      // onSnapshot just keeps showing the old status if this fails
    } finally {
      setPendingActionId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Reviews</h1>
        <div className={styles.headerRight}>
          {user?.email && <span className={styles.emailTag}>{user.email}</span>}
          <button className={styles.logoutBtn} onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>

      <AdminNav />

      {permissionError && (
        <div className={styles.banner}>
          Can't load reviews — Firestore rules probably don't allow signed-in
          reads yet on <code>reviews</code> and <code>reviewContacts</code>.
        </div>
      )}

      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label} {counts[tab.key] ? `(${counts[tab.key]})` : ""}
          </button>
        ))}
      </div>

      {!loading && !permissionError && filtered.length === 0 && (
        <p className={styles.empty}>No reviews here yet.</p>
      )}

      <div className={styles.list}>
        {filtered.map((r) => {
          const badgeClass = BADGE_CLASS[r.status] || "badgePending";
          const isBusy = pendingActionId === r.id;
          const phone = contacts[r.id];

          return (
            <div key={r.id} className={styles.row}>
              <div className={styles.rowTop}>
                <div className={reviewStyles.reviewRow}>
                  <span className={styles.name}>{r.name}</span>
                  <StarRating value={r.rating} size={16} />
                </div>
                <span className={`${styles.badge} ${styles[badgeClass]}`}>
                  {r.status}
                </span>
              </div>

              <div className={styles.metaRow}>
                {r.createdAt && <span>Sent {formatCreatedAt(r.createdAt)}</span>}
              </div>

              <div className={styles.notes}>{r.feedback}</div>

              {phone ? (
                <span className={reviewStyles.phoneTag}>
                  <LockIcon className={reviewStyles.phoneTagIcon} />
                  {phone}
                </span>
              ) : (
                <span className={styles.metaRow}>Contact number not loaded</span>
              )}

              <div className={styles.actions}>
                <button
                  className={styles.actionBtn}
                  disabled={isBusy || r.status === "published"}
                  onClick={() => handleStatusChange(r.id, "published")}
                >
                  Publish
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.actionDanger}`}
                  disabled={isBusy || r.status === "hidden"}
                  onClick={() => handleStatusChange(r.id, "hidden")}
                >
                  Hide
                </button>
                {r.status !== "pending" && (
                  <button
                    className={styles.actionBtn}
                    disabled={isBusy}
                    onClick={() => handleStatusChange(r.id, "pending")}
                  >
                    Reset to pending
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AdminReviews;
